import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { AgentView } from '@/features/agents/types'
import { useAppDispatch, useAppSelector } from '@/store'

import {
  agentSettingsKeyOf,
  type AgentAutonomyRung,
  type AgentSettingsDraft,
  type AgentSettingsDraftWrite,
  type AgentSettingsKey,
  type AgentOutputPermission,
  type ConnectorPermissionView,
  type FirmEffectiveAgentSettings,
} from '../types'
import {
  saveAgentSettingsDraft,
  selectAgentSettingsOperationError,
  selectAgentSettingsSaving,
  selectAgentSettingsVersions,
  selectFirmEffective,
  selectSelectedDraft,
} from '../slices/agentSettingsSlice'
import { serverKeyOf, useAgentSettingsLabels } from '../agentSettingsLabels'

const RUNG_OPTIONS: readonly AgentAutonomyRung[] = [
  'L1_SUGGESTS',
  'L2_DRAFTS',
  'L3_GUARDED_ACT',
  'L4_ACTS_AND_REPORTS',
]
const CLIENT_REASON_REQUIRED_KEY =
  'agent-settings.client-override.reason.required'

export interface AgentSettingsFormValues {
  autonomyRung: AgentAutonomyRung | ''
  costCeilingPerRunMinor: string
  costCeilingPerPhaseMinor: string
  currency: string
  secondReviewerRule: string
  escalationHours: string
  clientOverrideReason: string
  outputPermissions: AgentOutputPermission[]
}

type SettingsBaseline = AgentSettingsDraft | FirmEffectiveAgentSettings

const optionalNumber = (value: string): number | undefined => {
  if (value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const optionalText = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value.trim()

// eslint-disable-next-line react-refresh/only-export-components -- pure normalizer is unit-tested separately.
export const formValuesFor = (
  baseline: SettingsBaseline | null
): AgentSettingsFormValues => ({
  autonomyRung: baseline?.autonomyRung ?? '',
  costCeilingPerRunMinor: baseline?.costCeilingPerRunMinor?.toString() ?? '',
  costCeilingPerPhaseMinor:
    baseline?.costCeilingPerPhaseMinor?.toString() ?? '',
  currency: baseline?.currency ?? '',
  secondReviewerRule: baseline?.reviewPolicy?.secondReviewerRule ?? '',
  escalationHours: baseline?.reviewPolicy?.escalationHours?.toString() ?? '',
  clientOverrideReason: baseline?.clientOverrideReason ?? '',
  outputPermissions: baseline?.outputPermissions ?? [],
})

/** Builds the key-preserving, presence-aware write used by every editable rung. */
// eslint-disable-next-line react-refresh/only-export-components -- plan requires this tested pure write builder.
export const buildMinimalDraftWrite = (
  baseline: SettingsBaseline | null,
  form: AgentSettingsFormValues,
  key: AgentSettingsKey
): AgentSettingsDraftWrite | null => {
  const next = {
    autonomyRung: form.autonomyRung === '' ? undefined : form.autonomyRung,
    costCeilingPerRunMinor: optionalNumber(form.costCeilingPerRunMinor),
    costCeilingPerPhaseMinor: optionalNumber(form.costCeilingPerPhaseMinor),
    currency: optionalText(form.currency),
    reviewPolicy: {
      requiresNamedHumanMerge: true as const,
      secondReviewerRule: optionalText(form.secondReviewerRule),
      escalationHours: optionalNumber(form.escalationHours),
    },
    clientOverrideReason: optionalText(form.clientOverrideReason),
    outputPermissions: form.outputPermissions,
  }
  const current = baseline
  const reviewChanged =
    current?.reviewPolicy?.secondReviewerRule !==
      next.reviewPolicy.secondReviewerRule ||
    current?.reviewPolicy?.escalationHours !== next.reviewPolicy.escalationHours
  const changes = {
    ...(current?.autonomyRung === next.autonomyRung
      ? {}
      : { autonomyRung: next.autonomyRung ?? null }),
    ...(current?.costCeilingPerRunMinor === next.costCeilingPerRunMinor
      ? {}
      : { costCeilingPerRunMinor: next.costCeilingPerRunMinor ?? null }),
    ...(current?.costCeilingPerPhaseMinor === next.costCeilingPerPhaseMinor
      ? {}
      : { costCeilingPerPhaseMinor: next.costCeilingPerPhaseMinor ?? null }),
    ...(current?.currency === next.currency
      ? {}
      : { currency: next.currency ?? null }),
    ...(reviewChanged ? { reviewPolicy: next.reviewPolicy } : {}),
    ...(current?.clientOverrideReason === next.clientOverrideReason
      ? {}
      : { clientOverrideReason: next.clientOverrideReason ?? null }),
    ...(JSON.stringify(current?.outputPermissions ?? []) === JSON.stringify(next.outputPermissions)
      ? {}
      : { outputPermissions: next.outputPermissions }),
  }
  if (Object.keys(changes).length === 0) return null
  // Ceiling and currency are one unit on the server. A rung with no draft of
  // its own merges the write into an EMPTY row, so a ceiling sent without its
  // currency is refused even when the inherited baseline (the effective view)
  // shows one. With no draft, a write that carries a non-null ceiling
  // therefore carries the currency too; with a draft the server's
  // presence-aware merge keeps the stored currency and the body stays minimal.
  const hasDraft = baseline !== null && 'draftVersion' in baseline
  const carriesCeiling =
    !hasDraft &&
    (('costCeilingPerRunMinor' in changes &&
      changes.costCeilingPerRunMinor !== null) ||
      ('costCeilingPerPhaseMinor' in changes &&
        changes.costCeilingPerPhaseMinor !== null))
  const coupled =
    carriesCeiling && !('currency' in changes)
      ? { currency: next.currency ?? null }
      : {}
  return {
    scope: key.scope,
    ...(key.scopeKey === undefined ? {} : { scopeKey: key.scopeKey }),
    phase: key.phase,
    agentKey: key.agentKey,
    baseVersion:
      baseline !== null && 'draftVersion' in baseline
        ? baseline.draftVersion
        : 0,
    ...changes,
    ...coupled,
  }
}

function SettingsSection({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <section
      className="rounded-lg border border-border bg-card px-4 py-3"
      aria-label={label}
    >
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  )
}

export interface AgentSettingsFormProps {
  settingsKey: AgentSettingsKey
  agent: AgentView
  outputPermissions?: readonly ConnectorPermissionView[]
  onDirtyChange?: (dirty: boolean) => void
}

/** Controlled draft form: canonical data remains unchanged until save + re-read succeeds. */
export function AgentSettingsForm({
  settingsKey,
  agent,
  outputPermissions = [],
  onDirtyChange,
}: AgentSettingsFormProps) {
  const { t } = useTranslation('agents')
  const dispatch = useAppDispatch()
  const draft = useAppSelector((state) =>
    selectSelectedDraft(state, settingsKey)
  )
  const effective = useAppSelector((state) =>
    selectFirmEffective(state, settingsKey)
  )
  const saving = useAppSelector((state) =>
    selectAgentSettingsSaving(state, settingsKey)
  )
  const operationError = useAppSelector((state) =>
    selectAgentSettingsOperationError(state, settingsKey)
  )
  const versions = useAppSelector((state) =>
    selectAgentSettingsVersions(state, settingsKey)
  )
  const labels = useAgentSettingsLabels()
  const reasonRefused =
    operationError !== null &&
    serverKeyOf(operationError) === CLIENT_REASON_REQUIRED_KEY
  const baseline = draft ?? effective
  const [values, setValues] = useState(() => formValuesFor(baseline))
  const [notice, setNotice] = useState<string | null>(null)
  const alertRef = useRef<HTMLParagraphElement>(null)
  const cacheKey = agentSettingsKeyOf(settingsKey)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a successful authoritative reread resets local dirty inputs.
    setValues(formValuesFor(baseline))
    setNotice(null)
  }, [baseline, cacheKey])

  useEffect(() => {
    onDirtyChange?.(
      buildMinimalDraftWrite(baseline, values, settingsKey) !== null
    )
  }, [baseline, onDirtyChange, settingsKey, values])

  useEffect(() => {
    if (operationError !== null) alertRef.current?.focus()
  }, [operationError])

  const update = <K extends keyof AgentSettingsFormValues>(
    field: K,
    value: AgentSettingsFormValues[K]
  ) => setValues((current) => ({ ...current, [field]: value }))

  const toggleOutputPermission = (key: AgentOutputPermission) => setValues((current) => ({
    ...current,
    outputPermissions: current.outputPermissions.includes(key)
      ? current.outputPermissions.filter((item) => item !== key)
      : [...current.outputPermissions, key],
  }))

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const input = buildMinimalDraftWrite(baseline, values, settingsKey)
    if (input === null) {
      setNotice(t('configuration.noChanges'))
      return
    }
    setNotice(null)
    try {
      await dispatch(
        saveAgentSettingsDraft({ key: settingsKey, input })
      ).unwrap()
      setNotice(t('configuration.states.saved'))
    } catch {
      // The slice retains the readable baseline; controlled values stay local.
    }
  }

  const redacted = effective?.redacted === true
  const effectiveRung = effective?.autonomyRung

  return (
    <form onSubmit={onSubmit} className="mt-5">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="flex flex-col gap-4 xl:col-span-3">
          {agent.description !== undefined && (
            <SettingsSection label={t('configuration.fields.job')}>
              <p className="text-sm leading-relaxed text-ink-soft">
                {agent.description}
              </p>
            </SettingsSection>
          )}
          <SettingsSection label={t('configuration.fields.model')}>
            <dl className="space-y-1 text-sm">
              {agent.config?.provider !== undefined && (
                <div>
                  <dt className="sr-only">
                    {t('configuration.fields.provider')}
                  </dt>
                  <dd className="font-mono">{agent.config.provider}</dd>
                </div>
              )}
              {agent.config?.model !== undefined && (
                <div>
                  <dt className="sr-only">{t('configuration.fields.model')}</dt>
                  <dd className="font-mono">{agent.config.model}</dd>
                </div>
              )}
              {agent.config?.llmMode !== undefined && (
                <div>
                  <dt className="sr-only">{t('configuration.fields.mode')}</dt>
                  <dd>{agent.config.llmMode}</dd>
                </div>
              )}
              {agent.config === undefined && (
                <p className="text-muted-foreground">
                  {t('catalogue.unavailable')}
                </p>
              )}
            </dl>
          </SettingsSection>
          <SettingsSection label={t('configuration.fields.cost')}>
            <label className="block text-sm" htmlFor="agent-cost-run">
              {t('configuration.fields.costPerRun')}
            </label>
            <Input
              id="agent-cost-run"
              type="number"
              value={values.costCeilingPerRunMinor}
              disabled={redacted}
              onChange={(event) =>
                update('costCeilingPerRunMinor', event.target.value)
              }
            />
            <label className="block text-sm" htmlFor="agent-cost-phase">
              {t('configuration.fields.costPerPhase')}
            </label>
            <Input
              id="agent-cost-phase"
              type="number"
              value={values.costCeilingPerPhaseMinor}
              disabled={redacted}
              onChange={(event) =>
                update('costCeilingPerPhaseMinor', event.target.value)
              }
            />
            <label className="block text-sm" htmlFor="agent-currency">
              {t('configuration.fields.currency')}
            </label>
            <Input
              id="agent-currency"
              value={values.currency}
              disabled={redacted}
              onChange={(event) => update('currency', event.target.value)}
            />
            {redacted && (
              <p className="text-xs text-muted-foreground">
                {t('configuration.states.redacted')}
              </p>
            )}
          </SettingsSection>
        </div>
        <div className="flex flex-col gap-4 xl:col-span-5">
          <SettingsSection label={t('configuration.fields.autonomy')}>
            <label className="block text-sm" htmlFor="agent-autonomy">
              {t('configuration.fields.autonomy')}
            </label>
            <select
              id="agent-autonomy"
              value={values.autonomyRung}
              onChange={(event) =>
                update(
                  'autonomyRung',
                  event.target.value as AgentAutonomyRung | ''
                )
              }
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="">{t('labels.provenance.INHERITED')}</option>
              {RUNG_OPTIONS.map((rung) => (
                <option key={rung} value={rung}>
                  {labels.rung(rung)}
                </option>
              ))}
            </select>
            {effectiveRung !== undefined && (
              <p className="text-xs text-muted-foreground">
                {t('configuration.effectiveValue', {
                  value: labels.rung(effectiveRung),
                  provenance: labels.provenance(
                    effective?.provenance.autonomyRung ?? 'SYSTEM_DEFAULT'
                  ),
                })}
              </p>
            )}
          </SettingsSection>
          <SettingsSection label={t('configuration.fields.review')}>
            <p className="text-sm text-ink-soft">
              {t('configuration.namedHumanMergeLocked')}
            </p>
            <label className="block text-sm" htmlFor="agent-second-reviewer">
              {t('configuration.fields.secondReviewer')}
            </label>
            <Input
              id="agent-second-reviewer"
              value={values.secondReviewerRule}
              onChange={(event) =>
                update('secondReviewerRule', event.target.value)
              }
            />
          </SettingsSection>
          <SettingsSection label={t('configuration.fields.escalation')}>
            <label className="block text-sm" htmlFor="agent-escalation">
              {t('configuration.fields.escalation')}
            </label>
            <Input
              id="agent-escalation"
              type="number"
              value={values.escalationHours}
              onChange={(event) =>
                update('escalationHours', event.target.value)
              }
            />
          </SettingsSection>
          {settingsKey.scope === 'CLIENT' && (
            <SettingsSection label={t('guardrails.override.title')}>
              <label
                className="block text-sm"
                htmlFor="agent-client-override-reason"
              >
                {t('guardrails.override.reason')}
              </label>
              <textarea
                id="agent-client-override-reason"
                value={values.clientOverrideReason}
                aria-invalid={reasonRefused ? true : undefined}
                aria-describedby={
                  reasonRefused ? 'agent-settings-save-error' : undefined
                }
                className="min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm aria-[invalid=true]:border-destructive"
                onChange={(event) =>
                  update('clientOverrideReason', event.target.value)
                }
              />
            </SettingsSection>
          )}
          {outputPermissions.length > 0 && (
            <SettingsSection label={t('guardrails.outputs.title')}>
              {outputPermissions.map((permission) => (
                <label
                  key={permission.key}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={values.outputPermissions.includes(
                      permission.key as AgentOutputPermission
                    )}
                    disabled={!permission.overrideable}
                    onChange={() =>
                      toggleOutputPermission(
                        permission.key as AgentOutputPermission
                      )
                    }
                  />
                  <span data-permission={permission.key}>
                    {labels.outputPermission(permission.key)}
                  </span>
                  {!permission.overrideable && (
                    <span
                      className="text-xs text-muted-foreground"
                      data-refusal={permission.refusalReason ?? undefined}
                    >
                      {permission.refusalReason === null
                        ? t('guardrails.context.locked')
                        : labels.refusal(permission.refusalReason)}
                    </span>
                  )}
                </label>
              ))}
            </SettingsSection>
          )}
        </div>
        <div className="flex flex-col gap-4 xl:col-span-4">
          <SettingsSection label={t('configuration.fields.effectiveDraft')}>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-muted-foreground">
                  {t('configuration.fields.effective')}
                </dt>
                <dd>
                  {labels.effectiveSource(
                    effective,
                    versions,
                    settingsKey.scope
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  {t('configuration.fields.draft')}
                </dt>
                <dd>
                  {draft === null
                    ? t('configuration.noDraft')
                    : t('configuration.draftVersion', {
                        version: draft.draftVersion,
                      })}
                </dd>
              </div>
              {agent.metrics?.status !== undefined && (
                <div>
                  <dt className="text-muted-foreground">
                    {t('catalogue.columns.runtime')}
                  </dt>
                  <dd>{agent.metrics.status}</dd>
                </div>
              )}
              {labels.dateTime(agent.metrics?.lastActiveAt) !== null && (
                <div>
                  <dt className="text-muted-foreground">
                    {t('configuration.fields.runtimeLastActive')}
                  </dt>
                  <dd>{labels.dateTime(agent.metrics?.lastActiveAt)}</dd>
                </div>
              )}
            </dl>
          </SettingsSection>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="accent" disabled={saving}>
          {t('configuration.save')}
        </Button>
        {notice !== null && (
          <p role="status" className="text-sm text-muted-foreground">
            {notice}
          </p>
        )}
        {operationError !== null && (
          <p
            id="agent-settings-save-error"
            ref={alertRef}
            tabIndex={-1}
            role="alert"
            className="text-sm text-destructive"
            data-code={operationError.code}
          >
            {labels.serverMessage(operationError)}
          </p>
        )}
      </div>
    </form>
  )
}
